import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@headlessui/react';
import {
  PhoneArrowDownLeftIcon, XMarkIcon, ChevronRightIcon, ArrowPathIcon, ArrowLeftIcon, PhoneIcon,
  ChatBubbleLeftRightIcon, ChatBubbleOvalLeftEllipsisIcon, InformationCircleIcon,
} from '@heroicons/react/24/outline';
import {
  listFollowups, getFollowup, claimFollowup, attemptFollowup, closeFollowup, assignFollowup, listFollowupAssignees,
  redialFollowup, getFollowupCall,
} from '../../services/followupApi';
import {
  followupIdentity, sortQueue, isOverdue, ageSeconds, formatAge, formatWhen, relativeWhen, followupErrorKey, canCloseNoAnswer,
  isUnresolved, customerSaid, customerSentenceFromTranscript, sourceCallSid, sourceCallSids, latestSourceCallSid, whatsappDepartment, branchName,
} from './followups';
import CallTranscript from '../../components/CallTranscript';
import WhatsAppThreadPanel from '../whatsapp/WhatsAppThreadPanel';
import './advisorReview.css';

// td380 CA3f186acc: WHY the advisor must call (reason) + WHEN the customer asked (window).
// Keys are server enums; unknown values render verbatim rather than a missing-i18n key.
// Oct 19 (José client call): pregunta_fuera_de_alcance = an accepted advisor offer on an out-of-scope
// question; the row's customer_said carries the question itself ("el cliente preguntó por una preventiva").
const REASON_KEYS = ['horario_no_disponible', 'precio', 'asesor_solicitado', 'pregunta_fuera_de_alcance'];
const WINDOW_KEYS = { tarde: 'window_tarde', 'mañana': 'window_manana', manana: 'window_manana' };
const STATUS_KEYS = ['open', 'assigned', 'attended', 'no_answer', 'cancelled'];
export function reasonLabel(label, row) {
  const reason = String(row?.reason || '');
  const base = REASON_KEYS.includes(reason) ? label('reason_' + reason) : reason;
  const win = windowLabel(label, row);
  return win ? base + ' (' + win + ')' : base;
}
// Oct 16: motivo and window are shown as separate chips on the header card / list rows.
export function reasonOnly(label, row) {
  const reason = String(row?.reason || '');
  return REASON_KEYS.includes(reason) ? label('reason_' + reason) : reason;
}
export function windowLabel(label, row) {
  const raw = String(row?.requested_window || '').trim();
  if (!raw) return '';
  const key = WINDOW_KEYS[raw.toLowerCase()];
  return key ? label(key) : raw;
}
// Oct 18 (Roger 23:34): "El cliente dijo" never shows the internal placeholder. Order: the row's
// own sentence > the caller's sentence from the transcript > the request in words ("Prefiere por
// la tarde" / "No había horario disponible; pidió que un asesor le llame").
export function windowSentence(label, row) {
  const raw = String(row?.requested_window || '').trim();
  if (!raw) return '';
  const key = WINDOW_KEYS[raw.toLowerCase()];
  return key ? label(key === 'window_tarde' ? 'prefersAfternoon' : 'prefersMorning') : label('prefersWindowPrefix') + ' ' + raw;
}
export function fallbackSentence(label, row) {
  const reason = String(row?.reason || '');
  const base = label(REASON_KEYS.includes(reason) ? 'fallback_' + reason : 'fallback_asesor_solicitado');
  const win = windowSentence(label, row);
  return win ? base + ' · ' + win : base;
}
export function heardSentence(label, row, transcript) {
  const said = customerSaid(row) || customerSentenceFromTranscript(transcript);
  return said ? { text: '“' + said + '”', quoted: true } : { text: fallbackSentence(label, row), quoted: false };
}
// Status chip + tooltip (what the state means for the advisor).
export function statusHelp(label, row) {
  const status = String(row?.status || '');
  const key = STATUS_KEYS.includes(status) ? 'help_status_' + status : '';
  const base = key ? label(key) : '';
  return isOverdue(row) ? (base ? base + ' ' : '') + label('help_overdue') : base;
}

// Calendar "Llamadas pendientes" tray (live-handoff v3). Separate from appointment review.
// Shown to every signed-in role; the API scopes rows server-side (empty for callers without a grant).
export default function FollowupQueue({ user }) {
  const identity = followupIdentity(user);
  if (!identity) return null;
  return <QueueSession key={identity} identity={identity} />;
}

function QueueSession({ identity }) {
  const { t, i18n } = useTranslation();
  const label = (key) => t('followupQueue.' + key);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [selected, setSelected] = useState(null);
  const lastSelectedRequest = useRef(null);
  const queueButtons = useRef(new Map());
  const refreshButton = useRef(null);
  const restoreQueueFocus = useRef(false);
  // Oct 15 (td380): the API says whether this environment has a live-handoff store at all
  // (`store_configured === false` => "Bandeja no configurada", never the not-found message).
  const [storeConfigured, setStoreConfigured] = useState(true);

  useEffect(() => {
    if (!open) { restoreQueueFocus.current = false; return; }
    if (selected || loading || !restoreQueueFocus.current) return;
    const target = queueButtons.current.get(lastSelectedRequest.current)
      || queueButtons.current.values().next().value || refreshButton.current;
    target?.focus();
    restoreQueueFocus.current = false;
  }, [open, selected, loading]);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError('');
    listFollowups().then(({ data }) => {
      if (!alive) return;
      setStoreConfigured(data?.store_configured !== false);
      setRows(Array.isArray(data?.items) ? data.items : []);
    })
      .catch((err) => { if (alive) { setRows(null); setError(followupErrorKey(err)); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [identity, refresh]);

  const items = useMemo(() => sortQueue((rows || []).filter(isUnresolved)), [rows]);
  const overdueCount = items.filter((row) => isOverdue(row)).length;
  const reload = () => setRefresh((n) => n + 1);

  const queueContent = <>
    <div className="review-list-summary followup-list-summary">
      <span role="status">{!loading && !error && storeConfigured ? t('followupQueue.queueCount', { count: items.length, overdue: overdueCount }) : ''}</span>
      <button ref={refreshButton} type="button" className="inline-flex items-center gap-2" disabled={loading} onClick={reload}>
        <ArrowPathIcon className="h-4 w-4" />{label('refreshQueue')}
      </button>
    </div>
    <StatusLegend label={label} />
    {loading ? <p className="p-6" role="status">{label('loadingQueue')}</p>
      : error ? <p role="alert" className="p-6">{label(error)}</p>
        : !storeConfigured ? <div className="p-10 text-center" data-testid="followup-store-not-configured">
          <PhoneArrowDownLeftIcon className="mx-auto mb-3 h-10 w-10 review-muted" />
          <p className="font-medium" role="status">{label('storeNotConfigured')}</p>
          <p className="review-muted text-sm mt-1">{label('storeNotConfiguredHint')}</p>
        </div>
          : items.length === 0 ? <div className="p-10 text-center">
            <PhoneArrowDownLeftIcon className="mx-auto mb-3 h-10 w-10 text-emerald-500" />
            <p className="font-medium">{label('allClear')}</p>
            <p className="review-muted text-sm mt-1">{label('empty')}</p>
          </div>
            : <ul className="followup-queue-items" aria-label={label('queue')}>
              {items.map((row) => <li key={row.request_id}>
                <button type="button" className={'review-list-row followup-queue-row' + (selected === row.request_id ? ' followup-queue-row-selected' : '')}
                  ref={(node) => { if (node) queueButtons.current.set(row.request_id, node); else queueButtons.current.delete(row.request_id); }}
                  aria-pressed={selected === row.request_id} onClick={() => { lastSelectedRequest.current = row.request_id; setSelected(row.request_id); }}>
                  <div className="review-avatar" aria-hidden="true">{isOverdue(row) ? '!' : '☎'}</div>
                  <div className="min-w-0 flex-1">
                    <div className="followup-row-name">
                      <span>{row.contact_name || row.customer_phone_masked || label('unavailable')}</span>
                      {isOverdue(row) && <span className="followup-chip followup-chip-overdue">{label('overdue')}</span>}
                    </div>
                    <div data-testid="followup-row-chips">
                    <div className="followup-row-request">
                      {row.reason ? reasonOnly(label, row) : label('reason_asesor_solicitado')}
                      {windowLabel(label, row) && <span className="review-muted"> · {windowLabel(label, row)}</span>}
                    </div>
                    <div className="followup-row-meta">
                      <span className="followup-chip followup-chip-status" title={statusHelp(label, row)}>{label('status_' + row.status)}</span>
                      <span className="followup-chip">{t('followupQueue.ageChip', { age: formatAge(ageSeconds(row)) })}</span>
                      <span className="followup-chip followup-row-branch" data-testid="followup-row-branch">{branchName(row) || label('noBranch')}</span>
                      {row.assigned_name && <span className="followup-chip">{row.assigned_name}</span>}
                      {row.vehicle && <span className="followup-chip">{row.vehicle}</span>}
                      {Number(row.requests) > 1 && <span className="followup-chip">{t('followupQueue.requests', { count: row.requests })}</span>}
                    </div>
                    </div>
                    <span className="followup-row-phone review-muted">{row.customer_phone_masked}</span>
                  </div>
                  <ChevronRightIcon className="h-5 w-5 shrink-0 review-muted" />
                </button>
              </li>)}
            </ul>}
  </>;

  return <>
    <button type="button" className="review-trigger" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(true)}>
      <PhoneArrowDownLeftIcon className="h-5 w-5" />{label('pendingCalls')}
      <span className="review-count">{loading ? '…' : error ? '!' : !storeConfigured ? '–' : items.length}</span>
    </button>
    <Dialog open={open} onClose={() => { setOpen(false); setSelected(null); }} className="relative z-40">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" />
      <div className="fixed inset-0 followup-dialog-viewport">
        <Dialog.Panel className="review-list followup-dialog mx-auto">
          <div className="review-list-heading">
            <div>
              <Dialog.Title className="text-xl font-semibold">{label('queue')}</Dialog.Title>
              <p className="review-muted mt-1 text-sm" data-testid="followup-intro">{label('queueHint')}</p>
            </div>
            <button type="button" aria-label={label('closeList')} className="review-icon" onClick={() => { setOpen(false); setSelected(null); }}>
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <div className="followup-layout">
            <aside className={'followup-queue-pane' + (!selected ? ' followup-pane-active' : '')} aria-label={label('queue')}>
              {queueContent}
            </aside>
            {selected
              ? <div className="followup-detail-pane followup-pane-active" key={selected}>
                <FollowupDetail requestId={selected} label={label} locale={i18n?.language}
                  onBack={() => { restoreQueueFocus.current = true; setSelected(null); }} onChanged={reload} />
              </div>
              : <div className="followup-detail-empty">
                <PhoneArrowDownLeftIcon className="h-9 w-9" aria-hidden="true" />
                <p className="font-semibold">{label('selectRequest')}</p>
                <p className="review-muted text-sm">{label('selectRequestHint')}</p>
              </div>}
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  </>;
}

// Oct 18 (Roger 23:34): what each status chip means, one line each; 'Vencida' = the suggested
// deadline passed. Collapsed by default so the list stays compact.
function StatusLegend({ label }) {
  return <details className="followup-legend mx-6 mb-2" data-testid="followup-legend">
    <summary className="review-muted text-xs inline-flex items-center gap-1 cursor-pointer">
      <InformationCircleIcon className="h-4 w-4" />{label('legendTitle')}
    </summary>
    <dl className="followup-legend-list mt-2 text-xs">
      {STATUS_KEYS.map((s) => <div key={s} className="followup-legend-row">
        <dt><span className="followup-chip followup-chip-status">{label('status_' + s)}</span></dt>
        <dd className="review-muted">{label('help_status_' + s)}</dd>
      </div>)}
      <div className="followup-legend-row">
        <dt><span className="followup-chip followup-chip-overdue">{label('overdue')}</span></dt>
        <dd className="review-muted">{label('help_overdue')}</dd>
      </div>
    </dl>
  </details>;
}

// Call summaries arrive as model markdown ("- **Llamada de recordatorio**: …"); the asesor reads
// plain text, so the bold markers are dropped and bullets keep a readable dash (P1 cleanup, oct22).
export function plainSummary(value) {
  return String(value == null ? '' : value).replace(/\*\*/g, '').replace(/^\s*[-*]\s+/gm, '– ');
}

// One confirm dialog for every irreversible action (close / cancel / redial): says what will happen.
function ConfirmDialog({ open, testId, title, body, confirmLabel, cancelLabel, tone = 'primary', onConfirm, onCancel }) {
  return <Dialog open={open} onClose={onCancel} className="relative z-50">
    <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
    <div className="fixed inset-0 flex items-center justify-center p-4">
      <Dialog.Panel className="review-panel max-w-md p-6" data-testid={testId}>
        <Dialog.Title className="text-lg font-semibold">{title}</Dialog.Title>
        <p className="review-muted mt-2 text-sm whitespace-pre-line">{body}</p>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" className="followup-btn followup-btn-secondary" onClick={onCancel}>{cancelLabel}</button>
          <button type="button" className={'followup-btn followup-btn-' + tone} onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </Dialog.Panel>
    </div>
  </Dialog>;
}

function switchTab(event, current, setTab) {
  if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
  event.preventDefault();
  const next = event.key === 'Home' ? 'call' : event.key === 'End' ? 'whatsapp' : current === 'call' ? 'whatsapp' : 'call';
  setTab(next);
  document.getElementById('followup-tab-' + next)?.focus();
}

// Oct 16 (Roger 15:52, Calendar -> Llamadas pendientes): the detail reads like Revisiones — a header
// card (who / unit / branch / motivo / window / deadlines) + two tabs: "Llamada" (the originating call's
// transcript, with "Volver a llamar") and "WhatsApp" (the customer's thread with the reply box), then
// the grouped actions row (Asignación · Registrar intento · Cerrar solicitud).
// Oct 18 (Roger 23:34): every group explains itself (helper line + tooltips), the sucursal is a name,
// the deadlines read against the LATEST request, and close / cancel / redial confirm first.
function FollowupDetail({ requestId, label, locale, onBack, onChanged }) {
  const { t } = useTranslation();
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const [assignee, setAssignee] = useState('');
  const [assignees, setAssignees] = useState(null);
  const [tab, setTab] = useState('call');
  const [confirm, setConfirm] = useState(''); // '' | 'redial' | 'attended' | 'no_answer' | 'cancelled'
  const [redialNotice, setRedialNotice] = useState('');
  const detailHeading = useRef(null);
  // The originating call is loaded once here (not inside the tab) so "El cliente dijo" can quote
  // the transcript when the row carries no sentence.
  const [call, setCall] = useState(null);
  const [callState, setCallState] = useState('idle');

  const load = (keepError = false) => {
    setLoading(true); if (!keepError) setError('');
    return getFollowup(requestId).then(({ data }) => setRow(data))
      .catch((err) => { setRow(null); setError(followupErrorKey(err)); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [requestId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!loading && row) detailHeading.current?.focus();
  }, [loading, row?.request_id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!row?.can_assign || assignees !== null) return;
    listFollowupAssignees().then(({ data }) => setAssignees(Array.isArray(data?.items) ? data.items : []))
      .catch(() => setAssignees([]));
  }, [row, assignees]);
  const callSid = sourceCallSid(row);
  useEffect(() => {
    if (!callSid) { setCallState('missing'); setCall(null); return undefined; }
    let alive = true;
    setCallState('loading'); setCall(null);
    getFollowupCall(callSid).then(({ data }) => { if (alive) { setCall(data || {}); setCallState('ok'); } })
      .catch(() => { if (alive) setCallState('error'); });
    return () => { alive = false; };
  }, [callSid]);

  const act = (fn) => {
    setBusy(true); setError('');
    return fn().then(({ data }) => { setRow(data); setNote(''); setReason(''); onChanged(); return data; })
      .catch((err) => { setError(followupErrorKey(err)); if (err?.response?.status === 409 || err?.response?.status === 404) load(true); })
      .finally(() => setBusy(false));
  };
  const version = row?.version;
  const phone = row?.phone_visible && row?.customer_phone ? row.customer_phone : '';
  const heard = heardSentence(label, row, call?.transcript);
  // td423: the newest call (the one the customer just made) opens first; earlier calls stay one click away.
  const callSids = sourceCallSids(row);
  const latestCallSid = latestSourceCallSid(row);
  const windowText = windowLabel(label, row);
  const branch = branchName(row);
  const canRedial = Boolean(row?.can_act) && isUnresolved(row) && Boolean(row?.agent_id);
  const lastRequest = relativeWhen(row?.last_requested_at || row?.created_at, locale);
  const lastRequestText = lastRequest
    ? t('followupQueue.lastRequest_' + lastRequest.key, { time: lastRequest.time, date: lastRequest.date }) : '';

  const redial = () => {
    setConfirm(''); setRedialNotice('');
    return act(() => redialFollowup(requestId, { expected_version: version })).then((data) => {
      if (data?.redial?.call_sid) setRedialNotice(t('followupQueue.redialOk', { sid: data.redial.call_sid }));
    });
  };
  const close = (status) => {
    setConfirm('');
    return act(() => closeFollowup(requestId, { expected_version: version, status, ...(status === 'cancelled' ? { reason: reason.trim() } : {}) }));
  };
  const agentName = row?.agent_name || row?.agent_id || '';
  const dept = whatsappDepartment(row);
  // WA-TAB-24H-PICKER-oct19: prefill the out-of-window template variables from THIS request
  // (cliente, asesor = claimed advisor else the voice agent, sucursal, unidad, horario) so the
  // WhatsApp tab previews the exact text before "Enviar plantilla".
  const waTemplateVars = {
    cliente: String(row?.contact_name || '').trim().split(/\s+/)[0] || '',
    asesor: String(row?.assigned_name || row?.agent_name || '').trim(),
    sucursal: branch || '',
    unidad: String(row?.vehicle || '').trim(),
    horario: String(row?.requested_window || '').trim(),
    name: String(row?.contact_name || '').trim().split(/\s+/)[0] || '',
    branch: branch || '',
    vehicle: String(row?.vehicle || '').trim(),
    advisor: String(row?.assigned_name || '').trim(),
  };
  const confirmProps = {
    redial: { title: label('redialConfirmTitle'), tone: 'primary', confirmLabel: label('redialConfirm'), onConfirm: redial,
      body: t('followupQueue.redialConfirmBody', { phone: phone || row?.customer_phone_masked, agent: agentName || label('redialAgentFallback') }) },
    attended: { title: label('confirmAttendedTitle'), tone: 'primary', confirmLabel: label('confirmAttendedYes'), onConfirm: () => close('attended'),
      body: label('confirmAttendedBody') },
    no_answer: { title: label('confirmNoAnswerTitle'), tone: 'secondary', confirmLabel: label('confirmNoAnswerYes'), onConfirm: () => close('no_answer'),
      body: t('followupQueue.confirmNoAnswerBody', { count: row?.attempt_count || 0 }) },
    cancelled: { title: label('confirmCancelTitle'), tone: 'danger', confirmLabel: label('confirmCancelYes'), onConfirm: () => close('cancelled'),
      body: t('followupQueue.confirmCancelBody', { reason: reason.trim() }) },
  };

  return <section aria-label={label('detail')} className="followup-detail">
    <div className="p-6 pb-0">
      <button type="button" className="inline-flex items-center gap-2 mb-4" onClick={onBack}>
        <ArrowLeftIcon className="h-4 w-4" />{label('back')}
      </button>
    </div>
    {loading ? <p role="status" className="p-6 pt-0">{label('loadingDetail')}</p>
      : !row ? <p role="alert" className="p-6 pt-0">{label(error || 'notFound')}</p>
        : <>
          {/* ---- Header card ---- */}
          <header className="followup-header mx-6" data-testid="followup-header">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 ref={detailHeading} tabIndex={-1} className="text-lg font-semibold">{row.contact_name || label('customer')}</h3>
                <p className="review-muted mt-1 text-sm">
                  <PhoneIcon className="inline h-4 w-4 mr-1 align-text-bottom" aria-hidden="true" />
                  <span data-testid="followup-phone">{phone || row.customer_phone_masked}</span>
                  {row.vehicle ? <span data-testid="followup-vehicle"> · {row.vehicle}</span> : null}
                  {' · '}<span data-testid="followup-branch" title={label('branch')}>{branch || label('noBranch')}</span>
                </p>
              </div>
              <span className={'followup-chip ' + (isOverdue(row) ? 'followup-chip-overdue' : 'followup-chip-status')}
                title={statusHelp(label, row)} data-testid="followup-status">
                {label('status_' + row.status)}{isOverdue(row) ? ' · ' + label('overdue') : ''}
              </span>
            </div>
            <p className="mt-3 text-sm" data-testid="followup-reason">
              <strong>{label('reason')}:</strong> {row.reason ? reasonOnly(label, row) : label('reason_asesor_solicitado')}
              {' · '}<span className="review-muted">{windowText || label('noWindow')}</span>
            </p>
            <blockquote className="followup-said mt-2" data-testid="followup-heard">
              <span className="review-muted text-xs uppercase tracking-wide">{heard.quoted ? label('customerSaid') : label('customerAsked')}</span>
              <p className="mt-1 text-sm">{heard.text}</p>
            </blockquote>
            <div className="followup-chips mt-3" data-testid="followup-detail-chips">
              <span className="followup-chip" title={label('help_assigned')}>{label('assignedTo')}: {row.assigned_name || row.assigned_to || label('unassigned')}</span>
              <span className="followup-chip" title={label('help_attempts')}>{label('attempts')}: {row.attempt_count}</span>
            </div>
            <details className="followup-disclosure followup-metadata mt-3">
              <summary>{label('requestDetails')}</summary>
              <div className="followup-chips mt-3">
              <span className="followup-chip" title={label('help_age')}>{t('followupQueue.ageChip', { age: formatAge(ageSeconds(row)) })}</span>
              {row.claim_target && <span className="followup-chip" title={label('help_claimTarget')}>{t('followupQueue.claimChip', { when: formatWhen(row.claim_target, locale) })}</span>}
              {row.followup_target && <span className="followup-chip" title={label('help_followupTarget')}>{t('followupQueue.callChip', { when: formatWhen(row.followup_target, locale) })}</span>}
              <span className="followup-chip" title={label('help_requests')} data-testid="followup-requests-chip">
                <span data-testid="followup-requests">{label('requestsLabel')}: {row.requests}</span>{lastRequestText ? ' · ' + lastRequestText : ''}
              </span>
              {Number(row.requests) > 1 && row.last_requested_at && <span className="followup-chip" data-testid="followup-last-requested">
                {t('followupQueue.lastRequestedChip', { when: formatWhen(row.last_requested_at, locale) })}
              </span>}
              </div>
              {Array.isArray(row.attempts) && row.attempts.length > 0 && <div className="mt-3">
                <h4 className="review-muted text-xs font-semibold">{label('history')}</h4>
                <ul className="followup-attempt-history review-muted mt-2 text-xs">
                  {row.attempts.map((a, i) => <li key={i}>{formatWhen(a.at, locale)} · {a.by}{a.note ? ' · ' + a.note : ''}</li>)}
                </ul>
              </div>}
            </details>
            {!row.phone_visible && isUnresolved(row) && <p className="review-muted mt-2 text-xs">{label('phoneHidden')}</p>}
          </header>

          {/* ---- Tabs ---- */}
          <div role="tablist" aria-label={label('detail')} className="followup-tabs mx-6 mt-4">
            <button type="button" role="tab" id="followup-tab-call" aria-selected={tab === 'call'} aria-controls="followup-panel-call"
              tabIndex={tab === 'call' ? 0 : -1} onKeyDown={(event) => switchTab(event, tab, setTab)}
              className={'followup-tab' + (tab === 'call' ? ' followup-tab-active' : '')} onClick={() => setTab('call')}>
              <ChatBubbleLeftRightIcon className="h-4 w-4" />{label('tabCall')}
            </button>
            <button type="button" role="tab" id="followup-tab-whatsapp" aria-selected={tab === 'whatsapp'} aria-controls="followup-panel-whatsapp"
              tabIndex={tab === 'whatsapp' ? 0 : -1} onKeyDown={(event) => switchTab(event, tab, setTab)}
              className={'followup-tab' + (tab === 'whatsapp' ? ' followup-tab-active' : '')} onClick={() => setTab('whatsapp')}>
              <ChatBubbleOvalLeftEllipsisIcon className="h-4 w-4" />{label('tabWhatsapp')}
            </button>
          </div>

          {error && <p role="alert" className="mx-6 mt-3 text-sm">{label(error)}</p>}
          {redialNotice && <p role="status" className="mx-6 mt-3 text-sm review-success" data-testid="followup-redial-ok">{redialNotice}</p>}

          {tab === 'call' && <div role="tabpanel" id="followup-panel-call" aria-labelledby="followup-tab-call" className="mx-6 mt-3">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <h4 className="followup-section-label">{label('conversationContext')}</h4>
            </div>
            <CallTab callSids={callSids} initialCallSid={latestCallSid} label={label} />
          </div>}

          {tab === 'whatsapp' && <div role="tabpanel" id="followup-panel-whatsapp" aria-labelledby="followup-tab-whatsapp" className="mx-6 mt-3">
            {phone
              ? <>
                <p className="review-muted text-xs mb-2" data-testid="followup-wa-hint">{label(dept === 'postventa' ? 'waHintPostventa' : 'waHint')}</p>
                <div className="followup-wa" data-testid="followup-whatsapp">
                  <WhatsAppThreadPanel leadPhone={phone} department={dept} className="h-full" templateVars={waTemplateVars} />
                </div>
              </>
              : <p role="status" className="review-muted text-sm" data-testid="followup-whatsapp-hidden">{label('waHidden')}</p>}
          </div>}

          {/* ---- Actions row (grouped + labeled + explained) ---- */}
          {isUnresolved(row) && <div className="followup-actions mx-6 mt-5" data-testid="followup-actions">
            {(row.can_claim || row.can_assign) && <fieldset className="followup-action-group followup-assignment">
              <legend>{label('actionsAssign')}</legend>
              <p className="followup-action-help">{label('helpAssign')}</p>
              {row.can_claim && <button type="button" className="followup-btn followup-btn-primary" disabled={busy} title={label('tipClaim')}
                onClick={() => act(() => claimFollowup(requestId, { expected_version: version }))}>
                {label('claim')}
              </button>}
              {row.can_assign && <div className="followup-action-stack">
                <select aria-label={label('assignTo')} title={label('tipAssign')} value={assignee} onChange={(e) => setAssignee(e.target.value)}>
                  <option value="">{label('chooseAssignee')}</option>
                  {(assignees || []).map((u) => <option key={u.user_id} value={u.user_id}>{u.name || u.user_id}</option>)}
                </select>
                <button type="button" className={'followup-btn followup-btn-' + (row.can_claim ? 'secondary' : 'primary')} disabled={busy || !assignee} title={label('tipAssign')}
                  onClick={() => act(() => assignFollowup(requestId, { expected_version: version, assignee_user_id: assignee }))}>
                  {label('assign')}
                </button>
              </div>}
            </fieldset>}
            {row.can_act && <>
              <fieldset className="followup-action-group">
                <legend>{label('actionsAttempt')}</legend>
                <p className="followup-action-help">{label('helpAttempt')}</p>
                <div className="followup-action-stack">
                  <input aria-label={label('attemptNote')} value={note} onChange={(e) => setNote(e.target.value)} placeholder={label('attemptNotePlaceholder')} />
                  <button type="button" className="followup-btn followup-btn-secondary" disabled={busy} title={label('tipAttempt')}
                    onClick={() => act(() => attemptFollowup(requestId, { expected_version: version, note }))}>
                    {label('recordAttempt')}
                  </button>
                </div>
              </fieldset>
              <fieldset className="followup-action-group">
                <legend>{label('actionsClose')}</legend>
                <p className="followup-action-help">{label('helpClose')}</p>
                <div className="followup-action-stack">
                  <button type="button" className="followup-btn followup-btn-primary" disabled={busy} title={label('tipAttended')}
                    onClick={() => setConfirm('attended')}>
                    {label('closeAttended')}
                  </button>
                </div>
                <details className="followup-disclosure followup-other-actions">
                  <summary>{label('otherCloseOptions')}</summary>
                  <div className="followup-action-stack mt-3">
                  <button type="button" className="followup-btn followup-btn-secondary" disabled={busy || !canCloseNoAnswer(row)}
                    title={canCloseNoAnswer(row) ? label('tipNoAnswer') : t('followupQueue.attemptsRequired')}
                    onClick={() => setConfirm('no_answer')}>
                    {label('closeNoAnswer')}
                  </button>
                  {!canCloseNoAnswer(row) && <p className="followup-action-help">{label('attemptsRequired')}</p>}
                </div>
                <div className="followup-action-stack">
                  <input aria-label={label('cancelReason')} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={label('cancelReasonPlaceholder')} />
                  <button type="button" className="followup-btn followup-btn-danger" disabled={busy || !reason.trim()}
                    title={reason.trim() ? label('tipCancel') : label('reasonRequired')}
                    onClick={() => setConfirm('cancelled')}>
                    {label('closeCancelled')}
                  </button>
                </div>
                </details>
              </fieldset>
            </>}
          </div>}
          {canRedial && <div className="followup-automation mx-6 mt-4">
            <div><h4 className="text-sm font-medium">{label('automaticAgent')}</h4><p className="followup-action-help mt-1">{label('redialHelp')}</p></div>
            <button type="button" className="followup-redial" disabled={busy} data-testid="followup-redial"
              title={label('redialHelp')} onClick={() => setConfirm('redial')}>
              <PhoneIcon className="h-4 w-4" />{busy ? label('redialing') : label('redial')}
            </button>
          </div>}
          <div className="pb-6" />

          {/* ---- Confirm dialogs (redial / close / cancel) ---- */}
          {confirm && <ConfirmDialog open={Boolean(confirm)} testId={confirm === 'redial' ? 'followup-redial-confirm' : 'followup-confirm-' + confirm}
            cancelLabel={label('redialCancel')} onCancel={() => setConfirm('')} {...confirmProps[confirm]} />}
        </>}
  </section>;
}

// "Llamada" tab: the originating call (summary + transcript) via the same endpoint Call History uses.
// A caller without agent access to that call sees "unavailable", never an exception.
// td423: a request with several accepting calls (requests > 1) opens the NEWEST call first and lists the
// others as a small picker ("Llamada 1 · CAb498…", "Llamada 2 · CAb157… · más reciente").
function CallTab({ callSids, initialCallSid, label }) {
  const { t } = useTranslation();
  const sids = Array.isArray(callSids) ? callSids : [];
  const [selected, setSelected] = useState(initialCallSid || sids[sids.length - 1] || '');
  useEffect(() => { setSelected(initialCallSid || sids[sids.length - 1] || ''); }, [initialCallSid, sids.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps
  const callSid = selected;
  const [call, setCall] = useState(null);
  const [state, setState] = useState(callSid ? 'loading' : 'missing');
  useEffect(() => {
    if (!callSid) { setState('missing'); return undefined; }
    let alive = true;
    setState('loading'); setCall(null);
    getFollowupCall(callSid).then(({ data }) => { if (alive) { setCall(data || {}); setState('ok'); } })
      .catch(() => { if (alive) setState('error'); });
    return () => { alive = false; };
  }, [callSid]);
  const picker = sids.length > 1 && <div role="tablist" aria-label={label('callPicker')} className="followup-chips mb-2" data-testid="followup-call-picker">
    {sids.map((sid, i) => <button key={sid} type="button" role="tab" aria-selected={sid === callSid} data-testid={'followup-call-pick-' + i}
      className={'followup-chip' + (sid === callSid ? ' followup-chip-status' : '')} onClick={() => setSelected(sid)}>
      {t('followupQueue.callPick', { n: i + 1, sid: sid.slice(0, 10) })}{i === sids.length - 1 ? ' · ' + label('latestCall') : ''}
    </button>)}
  </div>;
  if (state === 'loading') return <>{picker}<p role="status" className="review-muted text-sm">{label('loadingTranscript')}</p></>;
  if (state === 'missing' || state === 'error') return <>{picker}<p role="status" className="review-muted text-sm" data-testid="followup-transcript-unavailable">{label('transcriptUnavailable')}</p></>;
  return <div className="followup-call" data-testid="followup-call" data-call-sid={callSid}>
    {picker}
    {call?.summary && <div className="followup-summary">
      <span className="review-muted text-xs uppercase tracking-wide">{label('callSummary')}</span>
      <p className="mt-1 text-sm whitespace-pre-wrap">{plainSummary(call.summary)}</p>
    </div>}
    <details key={callSid} className="followup-transcript followup-disclosure" data-testid="followup-transcript-disclosure">
      <summary>{label('transcript')}</summary>
      <CallTranscript transcript={call?.transcript} agentName={call?.agent_name} emptyText={label('noTranscript')} testId="followup-transcript" />
    </details>
  </div>;
}
